import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  TextField,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import { useNavigate } from "react-router-dom";
import usePrivateTimeCapsuleSend from "../../../../hooks/usePrivateTimeCapsuleSend";
import WizardMediaCarousel from "./components/WizardMediaCarousel";
import ForwardedFeaturedNotice from "./components/ForwardedFeaturedNotice";
import { isFeaturedCapsuleType } from "../../../../utils/timeCapsuleForward";
import { LightboxModal } from "../../../common/LightboxModal";
import SuccessDialog from "../../../SuccessDialog";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { auth, db } from "../../../../firebase";
import { alpha } from "@mui/material/styles";
import { ellipsizeProfileText } from "../../../../utils/profileText";
import { filterResolvableConnections } from "../../../../utils/connectionIdentity";
import {
  buildCapsuleShareMessage,
  getShareCopyVariant,
} from "../../../../utils/shareCopy";
import useUserProfileStore, {
  selectUserProfile,
} from "../../../../store/useUserProfileStore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

function normalizeConnection(record = {}) {
  return {
    uid: String(record.uid || "").trim(),
    displayName: String(record.displayName || "").trim(),
    email: String(record.email || "").trim(),
    phoneNumber: String(record.phoneNumber || "").trim(),
    profileImage: String(record.profileImage || "").trim(),
    // Not rendered, but kept so the identity backstop below can tell a real
    // handle-only account apart from a tombstone with nothing to show.
    userName: String(record.userName || "").trim(),
    publicHandle: String(record.publicHandle || "").trim(),
  };
}

function getConnectionPrimary(c) {
  return ellipsizeProfileText(c.displayName || c.email || c.phoneNumber || "Connection");
}

function getConnectionSecondary(c) {
  if (c.displayName) return ellipsizeProfileText(c.email || c.phoneNumber || "Inssa connection");
  if (c.email) return ellipsizeProfileText(c.phoneNumber || "Inssa connection");
  return ellipsizeProfileText("Inssa connection");
}

function getAvatarInitial(c) {
  const primary = getConnectionPrimary(c);
  return primary ? primary[0].toUpperCase() : "C";
}

function formatBusinessStartSummary(revealMode, scheduledReveal) {
  if (revealMode !== "schedule" || !scheduledReveal) {
    return "Starts now";
  }

  const date = scheduledReveal instanceof Date ? scheduledReveal : new Date(scheduledReveal);
  if (Number.isNaN(date.getTime())) return "Starts now";

  return `Starts ${date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

export default function Step3ShareFrictionless({
  wizardData,
  setWizardData,
  onBack,
  onSaveAndExit,
  isSavingDraft = false,
  onBuryComplete,
  onNavigationLockChange,
  onSubmissionStateChange,
  isBusinessAccount = false,
  isBusinessFlow = false,
  controllerOnly = false,
  openBuryDialogRequest = 0,
}) {
  const navigate = useNavigate();
  const [shareUrl, setShareUrl] = useState("");
  const [savedCapsuleId, setSavedCapsuleId] = useState(null);
  const [savedCapsulePreview, setSavedCapsulePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ message: "", severity: "success" });
  const initialRevealDate = wizardData.revealDate ? new Date(wizardData.revealDate) : null;
  const hasScheduledStart =
    initialRevealDate instanceof Date &&
    !Number.isNaN(initialRevealDate.getTime()) &&
    initialRevealDate.getTime() > Date.now();
  const [revealMode, setRevealMode] = useState(hasScheduledStart ? "schedule" : "immediate");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionsDialogOpen, setConnectionsDialogOpen] = useState(false);
  const [connectionsDialogStep, setConnectionsDialogStep] = useState("reveal");
  const [selectedConnectionUids, setSelectedConnectionUids] = useState(() => new Set());
  const [, setSaveAsPersonalMemory] = useState(() => wizardData.audience === "self");
  const [submittedAudience, setSubmittedAudience] = useState(null);
  const [connectionSearch, setConnectionSearch] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [autoShareNeedsOkFallback, setAutoShareNeedsOkFallback] = useState(false);
  const [scheduledReveal, setScheduledReveal] = useState(() => {
    if (hasScheduledStart) {
      return initialRevealDate;
    }
    const d = new Date();
    if (!isBusinessAccount) d.setHours(d.getHours() + 1);
    return d;
  });

  const effectiveWizardData = useMemo(
    () => ({
      ...wizardData,
      audience: isBusinessAccount ? "self" : wizardData.audience,
      isBusinessCapsule: isBusinessAccount || Boolean(wizardData.isBusinessCapsule),
      revealDate: revealMode === "schedule" ? scheduledReveal : null,
    }),
    [isBusinessAccount, revealMode, scheduledReveal, wizardData]
  );

  const actionBarPaddingBottom = useMemo(
    () => "calc(env(safe-area-inset-bottom) + 12px)",
    []
  );

  const {
    isSending,
    hasUpload,
    uploadProgress,
    error,
    clearError,
    send,
    errorMessage,
    showErrorSnack,
    setShowErrorSnack,
  } = usePrivateTimeCapsuleSend({
    wizardData: effectiveWizardData,
  });

  const sortedConnections = useMemo(() => {
    const copy = [...connections];
    copy.sort((a, b) => getConnectionPrimary(a).localeCompare(getConnectionPrimary(b)));
    return copy;
  }, [connections]);

  const filteredConnections = useMemo(() => {
    const q = connectionSearch.trim().toLowerCase();
    if (!q) return sortedConnections;
    return sortedConnections.filter((c) => {
      const target = `${c.displayName} ${c.email} ${c.phoneNumber}`.toLowerCase();
      return target.includes(q);
    });
  }, [sortedConnections, connectionSearch]);

  const allConnectionsSelected =
    sortedConnections.length > 0 &&
    sortedConnections.every((connection) => selectedConnectionUids.has(connection.uid));

  useEffect(() => {
    if (isBusinessAccount) {
      setConnections([]);
      setConnectionsLoading(false);
      return;
    }

    let active = true;
    (async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      setConnectionsLoading(true);
      try {
        const subSnap = await getDocs(collection(db, "users", uid, "connections"));
        const subIds = subSnap.docs.map((d) => d.id);

        const userSnap = await getDoc(doc(db, "users", uid));
        const arrIds = userSnap.data()?.connections || [];

        const allIds = Array.from(
          new Set([...subIds, ...arrIds].map((id) => String(id || "").trim()).filter(Boolean))
        );
        if (!allIds.length) {
          if (active) setConnections([]);
          return;
        }

        const profiles = (
          await Promise.all(
            allIds.map(async (id) => {
              const userRef = doc(db, "users", id);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                return normalizeConnection({
                  uid: userSnap.id,
                  displayName: userSnap.data()?.displayName,
                  email: userSnap.data()?.email,
                  phoneNumber: userSnap.data()?.phoneNumber,
                  profileImage: userSnap.data()?.profileImage,
                });
              }

              const byUidQuery = query(collection(db, "users"), where("uid", "==", id));
              const byUidSnap = await getDocs(byUidQuery);
              if (byUidSnap.empty) return null;
              const matched = byUidSnap.docs[0];
              return normalizeConnection({
                uid: matched.id,
                displayName: matched.data()?.displayName,
                email: matched.data()?.email,
                phoneNumber: matched.data()?.phoneNumber,
                profileImage: matched.data()?.profileImage,
              });
            })
          )
        ).filter(Boolean);

        if (active) {
          const uniqByUid = Array.from(new Map(profiles.map((p) => [p.uid, p])).values());
          setConnections(filterResolvableConnections(uniqByUid));
        }
      } catch (fetchErr) {
        console.error("Error loading connections for share step:", fetchErr);
        if (active) setConnections([]);
      } finally {
        if (active) setConnectionsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isBusinessAccount]);

  // Raw (unellipsized) display name; shareCopy sanitizes it before use.
  const senderProfile = useUserProfileStore(selectUserProfile);
  const senderDisplayName =
    senderProfile?.displayName || auth.currentUser?.displayName || "";
  const shareCopyVariant = getShareCopyVariant(wizardData);

  // Clipboard fallback carries the personalized message too — the share
  // text must never be a raw URL alone (FR-E2).
  const copyShareLink = useCallback(async (url) => {
    if (!url) return false;
    try {
      await navigator.clipboard.writeText(
        buildCapsuleShareMessage({
          senderName: senderDisplayName,
          variant: shareCopyVariant,
          shareUrl: url,
        })
      );
      setIsLinkCopied(true);
      return true;
    } catch {
      setIsLinkCopied(false);
      return false;
    }
  }, [senderDisplayName, shareCopyVariant]);

  const shareLink = useCallback(async (url, { showCopySnack = true } = {}) => {
    if (!url) return { ok: false, reason: "missing_url" };
    if (navigator.share) {
      try {
        // Embed the URL inside `text` rather than passing a separate `url`:
        // many native share targets (incl. the macOS share sheet) keep only
        // `url` and drop `text`, which would strip the personalized message.
        await navigator.share({
          title: wizardData.subject || "A time capsule for you",
          text: buildCapsuleShareMessage({
            senderName: senderDisplayName,
            variant: shareCopyVariant,
            shareUrl: url,
          }),
        });
        return { ok: true, method: "native" };
      } catch (err) {
        const errorName = String(err?.name || "").trim();
        return {
          ok: false,
          reason: errorName === "AbortError" ? "native_cancelled" : "native_failed",
          errorName,
        };
      }
    }
    if (!showCopySnack) return { ok: false, reason: "unsupported" };
    const copied = await copyShareLink(url);
    return { ok: copied, method: copied ? "clipboard" : "", reason: copied ? "" : "clipboard_failed" };
  }, [copyShareLink, wizardData.subject, senderDisplayName, shareCopyVariant]);

  const attemptAutomaticShare = useCallback(async (url) => {
    if (!url) return;
    const result = await shareLink(url, { showCopySnack: false });
    setAutoShareNeedsOkFallback(!result?.ok && result?.reason !== "native_cancelled");
  }, [shareLink]);

  const doSend = useCallback(async ({ recipientsForSend = [], audienceForSend } = {}) => {
    clearError();
    setAutoShareNeedsOkFallback(false);
    const recipientsForSubmission = isBusinessAccount ? [] : recipientsForSend;
    const finalAudience = isBusinessAccount ? "self" : (audienceForSend || wizardData.audience);
    const finalRevealDate = revealMode === "schedule" ? scheduledReveal : null;

    if (isBusinessAccount && wizardData.endDate) {
      const now = new Date();
      const endDateTime = new Date(wizardData.endDate);

      if (endDateTime < now) {
        setSnackbar({ message: "End date and time cannot be in the past.", severity: "error" });
        return null;
      }

      if (finalRevealDate) {
        const revealDateTime = new Date(finalRevealDate);
        if (endDateTime < revealDateTime) {
          setSnackbar({ message: "End date and time cannot be before the start date.", severity: "error" });
          return null;
        }
      }
    }

    setWizardData((prev) => ({
      ...prev,
      revealDate: finalRevealDate,
      recipients: recipientsForSubmission,
      audience: finalAudience,
    }));

    if (typeof onSubmissionStateChange === "function") {
      onSubmissionStateChange(true);
    }

    const result = await send({
      status: "sent",
      revealDateOverride: finalRevealDate,
      recipientsOverride: recipientsForSubmission,
      audienceOverride: finalAudience,
    });
    if (!result) {
      if (typeof onSubmissionStateChange === "function") {
        onSubmissionStateChange(false);
      }
      return;
    }

    setShareUrl(result.shareUrl || "");
    setSavedCapsuleId(result.capsuleId || null);
    setSavedCapsulePreview(result.capsulePreview || null);
    setSubmittedAudience(finalAudience);
    if (typeof onBuryComplete === "function") {
      onBuryComplete(result);
    }

    if (isBusinessAccount) {
      setSnackbar({ message: "Your sponsored capsule is live.", severity: "success" });
      return result;
    }

    if (finalAudience === "self") {
      setSnackbar({ message: "Your self capsule was buried.", severity: "success" });
      return result;
    }

    setSnackbar({
      message: recipientsForSubmission.length
        ? "Your time capsule was buried and sent to the selected contacts."
        : "Your time capsule was buried. Share the link when you're ready.",
      severity: "success",
    });
    return result;
  }, [clearError, isBusinessAccount, onBuryComplete, onSubmissionStateChange, revealMode, scheduledReveal, send, setWizardData, wizardData.audience, wizardData.endDate]);

  const closeConnectionsDialog = useCallback(() => {
    setConnectionsDialogOpen(false);
    setConnectionsDialogStep("reveal");
    setConnectionSearch("");
  }, []);

  const handleBuryNow = async () => {
    if (isBusinessAccount) {
      await doSend({ recipientsForSend: [] });
      return;
    }

    const selectedFromDraft = new Set(
      (wizardData.recipients || [])
        .map((r) => String(r?.uid || r?.userId || r?.id || "").trim())
        .filter(Boolean)
    );
    setSelectedConnectionUids(selectedFromDraft);
    setSaveAsPersonalMemory(false);
    setConnectionsDialogStep("reveal");
    setConnectionsDialogOpen(true);
  };

  useEffect(() => {
    if (!controllerOnly || !openBuryDialogRequest || isSending) return;
    handleBuryNow();
    // The request id is intentionally the trigger; handleBuryNow reads current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controllerOnly, openBuryDialogRequest]);

  const handleContinueFromRevealStep = async () => {
    if (isBusinessAccount) {
      closeConnectionsDialog();
      await doSend({ recipientsForSend: [] });
      return;
    }

    setConnectionsDialogStep("connections");
  };

  const handleToggleConnection = (uid) => {
    setSelectedConnectionUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleToggleAllConnections = () => {
    setSelectedConnectionUids((prev) => {
      if (allConnectionsSelected) {
        return new Set();
      }
      return new Set(sortedConnections.map((connection) => connection.uid));
    });
  };

  const handleSendToSelectedConnections = async () => {
    const recipientsForSend = connections
      .filter((c) => selectedConnectionUids.has(c.uid))
      .map((c) => ({
        uid: c.uid,
        userId: c.uid,
        id: c.uid,
        displayName: c.displayName,
        email: c.email,
        phone: c.phoneNumber,
      }));

    closeConnectionsDialog();
    const result = await doSend({
      recipientsForSend,
      audienceForSend: "share",
    });
    if (result?.shareUrl) {
      await attemptAutomaticShare(result.shareUrl);
    }
  };

  const openShare = async () => {
    return shareLink(shareUrl, { showCopySnack: true });
  };

  const openLightbox = (item, index = 0) => {
    if (!item?.url) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  useEffect(() => {
    if (!isLinkCopied) return undefined;
    const t = setTimeout(() => setIsLinkCopied(false), 1800);
    return () => clearTimeout(t);
  }, [isLinkCopied]);

  const currentAudience = submittedAudience || (isBusinessAccount ? "self" : wizardData.audience);
  const isDirectFinish = isBusinessAccount || currentAudience === "self";
  const hasCompletedSend = !isSending && Boolean(isDirectFinish ? savedCapsuleId : shareUrl);
  const readyToShare = hasCompletedSend && !isBusinessAccount && currentAudience !== "self";
  const canFinish = hasCompletedSend;
  const isUploading = isSending && hasUpload && uploadProgress < 100;
  const isBurying = isSending && !hasCompletedSend;
  const businessStartDateValue =
    revealMode === "schedule" ? scheduledReveal : new Date();
  const businessStartSummary = formatBusinessStartSummary(revealMode, scheduledReveal);
  const buryButtonText = (() => {
    if (error) return "Retry";
    if (isSending) {
      return isBusinessAccount ? "Publishing capsule..." : "Burying time capsule...";
    }
    return isBusinessAccount ? "Publish" : "Bury";
  })();
  const selectedContactsCount = selectedConnectionUids.size;
  const contactsFinishButtonText = (() => {
    if (isSending) return "Burying...";
    if (selectedContactsCount > 1) return `Bury, send to ${selectedContactsCount} contacts, then share more`;
    if (selectedContactsCount === 1) return "Bury, send to 1 contact, then share more";
    return "Bury, then choose who to share with";
  })();
  const successDialogPayload = useMemo(() => ({
    type: "capsule_buried",
    title: isBusinessAccount
      ? "Your sponsored capsule is live."
      : "Successfully buried!",
    subtitle: "",
    rewardPoints: 0,
    receiptId: savedCapsuleId ? `tc_drop_${savedCapsuleId}` : null,
    revealDate: savedCapsulePreview?.revealDate || null,
  }), [isBusinessAccount, savedCapsuleId, savedCapsulePreview?.revealDate]);

  const navigateHomeAfterBury = useCallback(() => {
    navigate("/", {
      state: {
        center: {
          lat: wizardData.lat,
          lng: wizardData.lng,
        },
        sentCapsuleId: savedCapsuleId,
        buriedCapsulePreview: savedCapsulePreview,
      },
    });
  }, [navigate, savedCapsuleId, savedCapsulePreview, wizardData.lat, wizardData.lng]);

  const handleSuccessDialogPrimaryAction = useCallback(async () => {
    if (!autoShareNeedsOkFallback || !shareUrl) return;
    const result = await shareLink(shareUrl, { showCopySnack: true });
    if (result?.ok) {
      setAutoShareNeedsOkFallback(false);
    }
  }, [autoShareNeedsOkFallback, shareLink, shareUrl]);

  useEffect(() => {
    if (typeof onNavigationLockChange !== "function") return undefined;
    onNavigationLockChange(isUploading);
    return () => onNavigationLockChange(false);
  }, [isUploading, onNavigationLockChange]);

  useEffect(() => {
    if (!hasCompletedSend || !savedCapsuleId || isBusinessAccount) return;
    setSuccessDialogOpen(true);
  }, [hasCompletedSend, isBusinessAccount, savedCapsuleId]);

  return (
    <Box sx={controllerOnly ? { display: "contents" } : { flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
      {!controllerOnly && (
      <>
        <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          px: 4,
          pt: 2,
          pb: 2,
        }}
      >
        {isFeaturedCapsuleType(wizardData?.forwardedFromType) && (
          <ForwardedFeaturedNotice
            title={wizardData?.forwardSourceTitle}
            avatarUrl={wizardData?.forwardSourceAvatarUrl}
          />
        )}
        <Typography
          variant="body2"
          sx={{ mb: 1.8, fontSize: "0.9rem", lineHeight: 1.35, "& strong": { fontWeight: 700 } }}
        >
          <strong>Subject:</strong> {wizardData.subject}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 1.2, fontSize: "0.9rem", lineHeight: 1.35, whiteSpace: "pre-wrap", "& strong": { fontWeight: 700 } }}
        >
          <strong>Message:</strong> {wizardData.message}
        </Typography>

        {(wizardData.mediaFiles || []).length > 0 && (
          <WizardMediaCarousel
            items={wizardData.mediaFiles || []}
            mediaHeight={250}
            onOpenLightbox={openLightbox}
          />
        )}

        {isSending && hasUpload && !hasCompletedSend && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.75, opacity: 0.8 }}>
              Uploading media: {uploadProgress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={(theme) => ({
                height: 8,
                borderRadius: 999,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.colors.blue, 0.18)
                    : undefined,
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.colors.blue
                      : undefined,
                },
              })}
            />
          </Box>
        )}

        {!hasCompletedSend ? (
          <Typography variant="caption" sx={{ mt: 0.8, mb: 1.2, opacity: 0.82, display: "block", lineHeight: 1.3 }}>
            {isBusinessAccount
              ? "Publish now, or choose a start date and optional end date for when this sponsored capsule appears."
              : wizardData.audience === "self"
                ? "Tap Bury to choose reveal timing and finish."
                : "Tap Bury to choose reveal timing, then pick connections or share by link."}
          </Typography>
        ) : isBusinessAccount ? (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sponsored capsule published successfully.
          </Typography>
        ) : wizardData.audience === "self" ? (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Capsule buried for yourself successfully.
          </Typography>
        ) : null}

        {isBusinessAccount && !hasCompletedSend && (
          <Box sx={{ mb: 2.25 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
              Availability window
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Start date & time"
                value={businessStartDateValue}
                onChange={(value) => {
                  if (!value) {
                    setRevealMode("immediate");
                    setWizardData((prev) => ({ ...prev, revealDate: null }));
                    return;
                  }

                  const nextDate = value instanceof Date ? value : new Date(value);
                  if (Number.isNaN(nextDate.getTime())) return;

                  setScheduledReveal(nextDate);
                  if (nextDate.getTime() <= Date.now()) {
                    setRevealMode("immediate");
                    setWizardData((prev) => ({ ...prev, revealDate: null }));
                    return;
                  }

                  setRevealMode("schedule");
                  setWizardData((prev) => ({ ...prev, revealDate: nextDate }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { mb: 1.5 },
                    helperText: "Defaults to now. Pick a future date to schedule the start.",
                  },
                }}
              />
              <DateTimePicker
                label="End date & time (optional)"
                value={wizardData.endDate || null}
                onChange={(value) => {
                  if (!value) {
                    setWizardData((prev) => ({ ...prev, endDate: null }));
                    return;
                  }

                  const nextDate = value instanceof Date ? value : new Date(value);
                  if (Number.isNaN(nextDate.getTime())) return;
                  setWizardData((prev) => ({
                    ...prev,
                    endDate: nextDate,
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: businessStartSummary,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}
        </Box>

        <LightboxModal
          items={wizardData.mediaFiles || []}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onClose={closeLightbox}
        />

        <Box
        sx={{
          flex: "0 0 auto",
          mt: "auto",
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          pt: 1.5,
          pb: actionBarPaddingBottom,
        }}
      >
        {readyToShare && (
          <Button
            variant={isLinkCopied ? "contained" : "outlined"}
            onClick={async () => {
              await copyShareLink(shareUrl);
            }}
            startIcon={<ContentCopyIcon />}
            sx={(theme) => ({
              mb: 1.25,
              width: "100%",
              justifyContent: "space-between",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              backgroundColor: isLinkCopied ? theme.colors.green : "transparent",
              color: isLinkCopied ? theme.colors.white : theme.palette.text.primary,
              borderColor: isLinkCopied ? theme.colors.green : theme.palette.divider,
              "&:hover": {
                backgroundColor:
                  isLinkCopied
                    ? theme.colors.green
                    : (theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.06)
                      : alpha(theme.palette.text.primary, 0.02)),
                borderColor: isLinkCopied ? theme.colors.green : theme.palette.divider,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                borderColor: theme.palette.action.disabledBackground,
              },
            })}
          >
            <Box sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography variant="caption" sx={{ display: "block", opacity: isLinkCopied ? 0.95 : 0.82 }}>
                {isLinkCopied ? "Link copied" : "Copy share link"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  opacity: isLinkCopied ? 0.98 : 0.9,
                }}
              >
                {shareUrl}
              </Typography>
            </Box>
          </Button>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hasCompletedSend
              ? (readyToShare ? "1fr 1fr" : "1fr")
              : (isBurying ? "1fr" : "auto 1.4fr 1fr"),
            gap: 1.5,
            width: "100%",
          }}
        >
          {!hasCompletedSend ? (
            <>
              {!isBurying && (
                <IconButton
                  onClick={onBack}
                  sx={(theme) => ({
                    borderRadius: "999px",
                    width: 48,
                    height: 48,
                    flex: "0 0 auto",
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.02)
                      : "transparent",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.06)
                          : alpha(theme.palette.text.primary, 0.02),
                      borderColor: theme.palette.divider,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.text.disabled,
                      borderColor: theme.palette.action.disabled,
                      backgroundColor: "transparent",
                    },
                  })}
                  aria-label="Back"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              {!isBurying && (
                <Button
                  variant="outlined"
                  onClick={onSaveAndExit}
                  disabled={isSending || isSavingDraft || typeof onSaveAndExit !== "function"}
                  startIcon={isSavingDraft ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={(theme) => ({
                    fontWeight: 700,
                    px: 2,
                    py: 1.25,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.primary, 0.02)
                      : "transparent",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.06)
                          : alpha(theme.palette.text.primary, 0.02),
                      borderColor: theme.palette.divider,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.text.disabled,
                      borderColor: theme.palette.action.disabledBackground,
                      backgroundColor: "transparent",
                    },
                  })}
                >
                  {isSavingDraft ? "Saving..." : "Save & exit"}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleBuryNow}
                disabled={isSending}
                endIcon={
                  isSending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Box component="img" src={IconNextArrow} alt="" sx={{ width: 16, height: 16 }} />
                  )
                }
                sx={(theme) => ({
                  fontWeight: 700,
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  px: 2,
                  py: 1.25,
                  whiteSpace: "nowrap",
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                })}
              >
                {buryButtonText}
              </Button>
            </>
          ) : (
            <>
              {readyToShare && (
                <Button
                  variant="contained"
                  onClick={openShare}
                  startIcon={<ShareIcon />}
                  sx={(theme) => ({
                    fontWeight: 700,
                    px: 2,
                    py: 1.25,
                    backgroundColor: theme.colors.blue,
                    color: theme.colors.white,
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  })}
                >
                  Share link
                </Button>
              )}

              <Button
                variant={readyToShare ? "outlined" : "contained"}
                onClick={navigateHomeAfterBury}
                disabled={!canFinish}
                startIcon={<HomeIcon />}
                sx={(theme) => ({
                  fontWeight: 700,
                  width: "100%",
                  px: 2,
                  py: 1.25,
                  ...(readyToShare
                    ? {
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.02)
                          : "transparent",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.text.primary, 0.06)
                            : alpha(theme.palette.text.primary, 0.02),
                        borderColor: theme.palette.divider,
                      },
                      "&.Mui-disabled": {
                        color: theme.palette.text.disabled,
                        borderColor: theme.palette.action.disabledBackground,
                        backgroundColor: "transparent",
                      },
                    }
                    : {}),
                  ...(readyToShare
                    ? {}
                    : {
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }),
                })}
              >
                Home
              </Button>
            </>
          )}
        </Box>
        </Box>
      </>
      )}

      {controllerOnly && (isSending || hasCompletedSend) && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderTopColor: "divider",
            px: 2,
            pt: 1.5,
            pb: actionBarPaddingBottom,
          }}
        >
          {!hasCompletedSend ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: hasUpload ? 1.2 : 0 }}>
                <CircularProgress size={18} color="inherit" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    Burying your time capsule...
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.75, lineHeight: 1.25 }}>
                    Keep this screen open for just a moment.
                  </Typography>
                </Box>
              </Box>
              {hasUpload && (
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={(theme) => ({
                    height: 8,
                    borderRadius: 999,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.colors.blue, 0.18)
                        : undefined,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.colors.blue
                          : undefined,
                    },
                  })}
                />
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.25, fontWeight: 700 }}>
                Your time capsule was buried.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: readyToShare ? "1fr 1fr" : "1fr",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                {readyToShare && (
                  <Button
                    variant="contained"
                    onClick={openShare}
                    startIcon={<ShareIcon />}
                    sx={(theme) => ({
                      fontWeight: 700,
                      px: 2,
                      py: 1.25,
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                    })}
                  >
                    Share link
                  </Button>
                )}
                <Button
                  variant={readyToShare ? "outlined" : "contained"}
                  onClick={navigateHomeAfterBury}
                  disabled={!canFinish}
                  startIcon={<HomeIcon />}
                  sx={(theme) => ({
                    fontWeight: 700,
                    width: "100%",
                    px: 2,
                    py: 1.25,
                    ...(readyToShare
                      ? {
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.text.primary, 0.02)
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.text.primary, 0.06)
                              : alpha(theme.palette.text.primary, 0.02),
                          borderColor: theme.palette.divider,
                        },
                      }
                      : {
                        backgroundColor: theme.colors.blue,
                        color: theme.colors.white,
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                      }),
                  })}
                >
                  Home
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      <Dialog
        open={connectionsDialogOpen}
        onClose={closeConnectionsDialog}
        fullWidth
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: (theme) => alpha(theme.palette.background.default, 0.62),
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
          "& .MuiDialog-paper": {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="div" variant="h6" sx={{ fontWeight: 700 }}>
            {connectionsDialogStep === "reveal"
              ? (isBusinessAccount ? "Publish settings" : "Reveal settings")
              : "Send or save"}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
            {isBusinessAccount
              ? "Final step"
              : connectionsDialogStep === "reveal"
                ? "Step 1 of 2"
                : `${selectedConnectionUids.size} selected · Step 2 of 2`}
          </Typography>
        </DialogTitle>
        {connectionsDialogStep === "reveal" ? (
          <>
            <DialogContent dividers>
              {isBusinessAccount && (
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                  This publishes as a sponsored capsule and skips link sharing for now.
                </Typography>
              )}

              <Typography variant="caption" sx={{ display: "block", mb: 0.75, opacity: 0.8 }}>
                {isBusinessAccount ? "Start date" : "Reveal timing"}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                <Button
                  onClick={() => setRevealMode("immediate")}
                  startIcon={<BoltRoundedIcon />}
                  endIcon={
                    revealMode === "immediate" ? (
                      <CheckCircleRoundedIcon sx={(theme) => ({ color: theme.colors.green })} />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />
                    )
                  }
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    color: theme.palette.text.primary,
                    borderRadius: 1,
                    border: revealMode === "immediate" ? "2px solid" : "1px solid",
                    borderColor:
                      revealMode === "immediate" ? theme.colors.green : "divider",
                    backgroundColor:
                      revealMode === "immediate" ? alpha(theme.colors.green, 0.12) : "transparent",
                    pl: 2,
                    pr: 1.25,
                    py: 1,
                    minHeight: 72,
                    "& .MuiButton-startIcon": { ml: 0, mr: 1.5 },
                    "& .MuiButton-endIcon": { ml: "auto" },
                  })}
                >
                  <Box sx={{ textAlign: "left", flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isBusinessAccount ? "Start now" : "Reveal now"}
                    </Typography>
                  </Box>
                </Button>
                <Button
                  onClick={() => setRevealMode("schedule")}
                  startIcon={<EventRoundedIcon />}
                  endIcon={
                    revealMode === "schedule" ? (
                      <CheckCircleRoundedIcon sx={(theme) => ({ color: theme.colors.green })} />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />
                    )
                  }
                  sx={(theme) => ({
                    justifyContent: "flex-start",
                    color: theme.palette.text.primary,
                    borderRadius: 1,
                    border: revealMode === "schedule" ? "2px solid" : "1px solid",
                    borderColor:
                      revealMode === "schedule" ? theme.colors.green : "divider",
                    backgroundColor:
                      revealMode === "schedule" ? alpha(theme.colors.green, 0.12) : "transparent",
                    pl: 2,
                    pr: 1.25,
                    py: 1,
                    minHeight: 72,
                    "& .MuiButton-startIcon": { ml: 0, mr: 1.5 },
                    "& .MuiButton-endIcon": { ml: "auto" },
                  })}
                >
                  <Box sx={{ textAlign: "left", flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isBusinessAccount ? "Schedule start" : "Reveal later"}
                    </Typography>
                  </Box>
                </Button>
              </Box>

              {revealMode === "schedule" && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label={isBusinessAccount ? "Start date & time" : "Reveal date & time"}
                    value={scheduledReveal}
                    onChange={(v) => v && setScheduledReveal(v)}
                    minDateTime={new Date()}
                    slotProps={{ textField: { fullWidth: true, sx: { mt: 2.5 } } }}
                  />
                </LocalizationProvider>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleContinueFromRevealStep}
                  disabled={isSending}
                  sx={(theme) => ({
                    py: 1.1,
                    backgroundColor: theme.colors.blue,
                    color: theme.colors.white,
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  })}
                >
                  {isBusinessAccount ? "Publish now" : "Continue"}
                </Button>
                <Button
                  fullWidth
                  onClick={closeConnectionsDialog}
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                >
                  Cancel
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers sx={{ p: 0 }}>
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  px: 2,
                  pt: 1.5,
                  pb: 1.25,
                  backgroundColor: "background.paper",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" sx={{ display: "block", mb: 0.75, opacity: 0.75 }}>
                  Choose contacts now. After burying, your phone will open sharing so you can send it to anyone else.
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search by name or email"
                  value={connectionSearch}
                  onChange={(e) => setConnectionSearch(e.target.value)}
                  sx={(theme) => ({
                    mt: 1.25,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "999px",
                      ...(theme.palette.mode === "dark"
                        ? {
                          backgroundColor: alpha(theme.palette.background.default, 0.65),
                        }
                        : {}),
                    },
                  })}
                />
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    {sortedConnections.length
                      ? `${selectedConnectionUids.size} of ${sortedConnections.length} contacts selected`
                      : "No saved contacts yet"}
                  </Typography>
                  {sortedConnections.length > 0 && (
                    <Button
                      size="small"
                      onClick={handleToggleAllConnections}
                      sx={{ minWidth: 0, px: 0, fontWeight: 700 }}
                    >
                      {allConnectionsSelected ? "Clear all" : "Select all"}
                    </Button>
                  )}
                </Box>
              </Box>

              <List disablePadding sx={{ maxHeight: "46vh", overflowY: "auto", pb: 1 }}>
                {filteredConnections.map((c) => {
                  const selected = selectedConnectionUids.has(c.uid);
                  return (
                    <ListItemButton
                      key={c.uid}
                      selected={selected}
                      onClick={() => handleToggleConnection(c.uid)}
                      sx={(theme) => ({
                        py: 1.1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: alpha(theme.colors.green, 0.12),
                        },
                        "&:hover": {
                          backgroundColor: selected
                            ? alpha(theme.colors.green, 0.12)
                            : alpha(theme.palette.text.primary, 0.03),
                        },
                      })}
                    >
                      <ListItemAvatar>
                        <Avatar src={c.profileImage || undefined}>{getAvatarInitial(c)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={getConnectionPrimary(c)}
                        secondary={getConnectionSecondary(c)}
                        primaryTypographyProps={{
                          sx: { maxWidth: "230px", whiteSpace: "normal", wordBreak: "break-word" },
                        }}
                        secondaryTypographyProps={{
                          sx: { maxWidth: "230px", whiteSpace: "normal", wordBreak: "break-word" },
                        }}
                      />
                      {selected ? (
                        <CheckCircleRoundedIcon sx={(theme) => ({ color: theme.colors.green })} />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />
                      )}
                    </ListItemButton>
                  );
                })}

                {!filteredConnections.length && (
                  <Typography variant="body2" sx={{ px: 2, py: 1.5 }}>
                    {connectionsLoading ? "Loading connections..." : "No connections match your search."}
                  </Typography>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSendToSelectedConnections}
                  disabled={isSending}
                  sx={(theme) => ({
                    py: 1.1,
                    backgroundColor: theme.colors.green,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.getContrastText(theme.palette.success.main)
                        : theme.colors.white,
                    "&:hover": {
                      backgroundColor: theme.colors.green,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  })}
                >
                  {contactsFinishButtonText}
                </Button>
                <Button
                  fullWidth
                  onClick={() => setConnectionsDialogStep("reveal")}
                  sx={(theme) => ({
                    color: theme.palette.mode === "dark" ? theme.palette.text.primary : theme.colors.blue,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.06)
                          : alpha(theme.colors.blue, 0.06),
                    },
                  })}
                >
                  Back
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={Boolean(snackbar.message)} autoHideDuration={4000} onClose={() => setSnackbar({ message: "", severity: "success" })}>
        <Alert
          onClose={() => setSnackbar({ message: "", severity: "success" })}
          severity={snackbar.severity}
          variant="filled"
          sx={(theme) => ({
            width: "100%",
            ...(snackbar.severity === "success"
              ? {
                backgroundColor: theme.colors.green,
                color: theme.colors.white,
              }
              : {}),
          })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar open={showErrorSnack} autoHideDuration={6000} onClose={() => setShowErrorSnack(false)}>
        <Alert onClose={() => setShowErrorSnack(false)} severity="error" variant="filled" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {successDialogOpen && (
        <SuccessDialog
          sentCapsuleId={savedCapsuleId}
          trigger={successDialogOpen}
          payload={successDialogPayload}
          onPrimaryAction={autoShareNeedsOkFallback ? handleSuccessDialogPrimaryAction : undefined}
          onClose={navigateHomeAfterBury}
        />
      )}
    </Box>
  );
}
